/**
 * Tiny scroll-position memory keyed by route.
 *
 * Usage:
 *   import { rememberScroll, restoreScroll } from "../lib/scrollMemory";
 *
 *   // Before navigating away from a list page:
 *   rememberScroll(location.pathname);
 *
 *   // On the list page's mount effect:
 *   restoreScroll(location.pathname);
 *
 * Uses sessionStorage so the memory is naturally scoped to a single tab visit
 * (and disappears when the user closes the tab — no stale state).
 */

import { logger } from "./logger";

const PREFIX = "iss-scroll:";

export function rememberScroll(key) {
  try {
    sessionStorage.setItem(`${PREFIX}${key}`, String(window.scrollY || 0));
  } catch (e) {
    logger.silent("rememberScroll", e);
  }
}

/**
 * Restore the saved scroll for `key`. Returns true if a position was restored.
 * Clears the entry after consuming it so a hard refresh keeps the natural
 * "top of page" behaviour.
 */
export function restoreScroll(key) {
  try {
    const raw = sessionStorage.getItem(`${PREFIX}${key}`);
    if (raw == null) return false;
    sessionStorage.removeItem(`${PREFIX}${key}`);
    const y = parseInt(raw, 10);
    if (Number.isFinite(y) && y > 0) {
      requestAnimationFrame(() => window.scrollTo({ top: y, behavior: "auto" }));
      return true;
    }
  } catch (e) {
    logger.silent("restoreScroll", e);
  }
  return false;
}
